import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import "./SalesPageNew.css";

const S3_BASE = 'https://openaccessimages.s3.eu-west-2.amazonaws.com/sales-page-assets/images/social';
const LOGO_BASE = 'https://openaccessimages.s3.eu-west-2.amazonaws.com/sales-page-assets/images';
const socialLindsey = `${S3_BASE}/lindsey.webp`;
const socialOlivia = `${S3_BASE}/olivia.webp`;
const socialKeto = `${S3_BASE}/keto.webp`;
const socialAbi = `${S3_BASE}/abi.webp`;
const socialTegan = `${S3_BASE}/tegan.webp`;
const socialWilliam = `${S3_BASE}/william.webp`;
const socialGemma = `${S3_BASE}/gemma.webp`;
const socialLisa = `${S3_BASE}/lisa.webp`;
const withmeBubbleLogo = '/logo_purple_withme.png';

const bubbles = [
   { src: socialLindsey, left: 20, size: 120, duration: 7,  delay: -1,  mobile: true  },
   { src: socialOlivia,  left: 28, size: 200, duration: 10, delay: -6,  mobile: false },
   { src: socialKeto,    left: 36, size: 160, duration: 8,  delay: -3,  mobile: true  },
   { src: socialAbi,     left: 46, size: 280, duration: 11, delay: -8,  mobile: true  },
   { src: socialTegan,   left: 56, size: 140, duration: 6,  delay: -2,  mobile: false },
   { src: socialWilliam, left: 64, size: 220, duration: 10, delay: -5,  mobile: true  },
   { src: socialGemma,   left: 72, size: 180, duration: 9,  delay: -7,  mobile: false },
   { src: socialLisa,    left: 80, size: 250, duration: 11, delay: -9,  mobile: true  },
   { src: withmeBubbleLogo, left: 14, size: 96,  duration: 7,  delay: -2,  mobile: true,  isLogo: true },
   { src: withmeBubbleLogo, left: 86, size: 88,  duration: 8,  delay: -5,  mobile: false, isLogo: true },
];

function FloatingBubbles() {
   const containerRef = useRef(null);
   const [started, setStarted] = useState(false);
   const [isMobile, setIsMobile] = useState(false);

   useEffect(() => {
      const mq = window.matchMedia("(max-width: 767px)");
      const update = () => setIsMobile(mq.matches);
      update();
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
   }, []);

   useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const io = new IntersectionObserver(
         (entries) => { entries.forEach((e) => { if (e.isIntersecting) { setStarted(true); io.disconnect(); } }); },
         { threshold: 0.2 }
      );
      io.observe(el);
      return () => io.disconnect();
   }, []);

   return (
      <div
         ref={containerRef}
         style={{ pointerEvents: 'none', position: 'absolute', inset: 0, height: '100%', width: '100%', overflow: 'hidden', zIndex: 0 }}
      >
         {bubbles.map((b, i) => {
            const size = isMobile ? Math.round(b.size * 0.5) : b.size;
            const left = isMobile ? 50 + (b.left - 50) * 0.45 : b.left;
            if (!b.mobile && isMobile) return null;
            return (
               <div
                  key={i}
                  style={{
                     position: 'absolute', bottom: 0,
                     left: `${left}%`,
                     width: size, height: size,
                     marginLeft: -size / 2,
                     animation: `float-up-bubble ${b.duration}s linear ${b.delay}s infinite`,
                     animationPlayState: started ? 'running' : 'paused',
                     opacity: started ? undefined : 0,
                     willChange: 'transform, opacity',
                  }}
               >
                  <div style={{
                     height: '100%', width: '100%', overflow: 'hidden', borderRadius: '50%',
                     boxShadow: '0 12px 30px -12px rgba(15,23,42,0.25)', background: '#fff',
                     border: '1px solid rgba(0,0,0,0.05)',
                     padding: b.isLogo ? 8 : 0,
                  }}>
                     <img
                        src={b.src} alt="" loading="lazy"
                        style={{
                           width: '100%', height: '100%',
                           objectFit: b.isLogo ? 'contain' : 'cover',
                        }}
                     />
                  </div>
               </div>
            );
         })}
         <div style={{ pointerEvents: 'none', position: 'absolute', top: 0, left: 0, width: '100%', height: 128, background: 'linear-gradient(to bottom, #fff, transparent)', zIndex: 10 }} />
         <div style={{ pointerEvents: 'none', position: 'absolute', bottom: 0, left: 0, width: '100%', height: 128, background: 'linear-gradient(to top, #fff, transparent)', zIndex: 10 }} />
      </div>
   );
}

export default function CounterSection({ windowWidth }) {
   const { t } = useTranslation();
   const ref = useRef(null);
   const [count, setCount] = useState(0);

   useEffect(() => {
      const el = ref.current;
      if (!el) return;
      let rafId = 0;
      let isAnimating = false;
      const animate = () => {
         isAnimating = true;
         const target = 8000;
         const duration = 1200;
         const start = performance.now();
         setCount(0);
         const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setCount(Math.round(target * eased));
            if (t < 1) rafId = requestAnimationFrame(tick);
            else isAnimating = false;
         };
         rafId = requestAnimationFrame(tick);
      };
      const io = new IntersectionObserver(
         entries => {
            entries.forEach(e => {
               if (e.isIntersecting) { if (!isAnimating) animate(); }
               else { cancelAnimationFrame(rafId); isAnimating = false; setCount(0); }
            });
         },
         { threshold: 0.4 },
      );
      io.observe(el);
      return () => { cancelAnimationFrame(rafId); io.disconnect(); };
   }, []);

   const isSmall = windowWidth < 640;
   const isMobile = windowWidth < 768;
   return (
      <section style={{ position: 'relative', display: 'flex', minHeight: isSmall ? '60vh' : '80vh', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#fff', padding: '96px 0', color: 'var(--sp-ink)' }}>
         <FloatingBubbles />
         <div style={{ pointerEvents: 'none', position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: '100%', maxWidth: 960, padding: '0 24px', textAlign: 'center', zIndex: 20 }}>
            <p ref={ref} className="sp-tabular-nums" style={{
               whiteSpace: 'nowrap',
               fontSize: isSmall ? 48 : (isMobile ? 72 : 96),
               fontWeight: 800, letterSpacing: '-0.02em',
               margin: 0,
               backgroundImage: 'linear-gradient(to right, var(--brand-color), var(--brand-color))',
               WebkitBackgroundClip: 'text',
               WebkitTextFillColor: 'transparent',
               backgroundClip: 'text',
            }}>
               {count.toLocaleString('en-US')}+
            </p>
            <p style={{ marginTop: 16, marginBottom: 0, fontSize: 18, color: 'var(--sp-text-soft)', textTransform: 'none', fontWeight: 400, textShadow: '0 1px 8px rgba(255,255,255,0.9)' }}>
               {t('salesPageNew.counter.description')}
            </p>
         </div>
      </section>
   );
}
